import { motion } from 'framer-motion';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ChevronLeft, Star, Sun, Moon, Compass, LayoutGrid, Orbit, Sparkles, Lock, ArrowRight, LogIn, User, CloudSun, Target, Crown } from 'lucide-react';
import { useAuth } from '@/components/AuthContext';
import PlaylistToggle from '@/components/PlaylistToggle';
import QuizDiscoverChatSection from '@/components/QuizDiscoverChatSection';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MODULES = [
  {
    id: 'horoscope',
    title: 'Horoscope du Jour',
    subtitle: 'Ta guidance quotidienne',
    description: 'Ton horoscope personnalisé chaque jour par Stella',
    icon: Crown,
    path: '/astrozodiac/horoscope',
    status: 'available',
    isFree: false,
    isPremiumHighlight: true,
    gradient: 'from-amber-500 to-rose-500',
    features: ['Personnalisé', 'Quotidien', 'Conseils du jour']
  },
  {
    id: 'big-three',
    title: 'Les 3 Piliers',
    subtitle: 'Soleil, Lune, Ascendant',
    description: 'Ton identité essentielle en 3 points clés',
    icon: Sun,
    path: '/astrozodiac/big-three',
    status: 'available',
    isFree: true,
    gradient: 'from-amber-500 to-orange-500',
    features: ['Soleil = Identité', 'Lune = Émotions', 'Ascendant = Image']
  },
  {
    id: 'planets',
    title: 'Forces Planétaires',
    subtitle: '10 planètes = 10 forces',
    description: 'Tes compétences et zones de travail',
    icon: Orbit,
    path: '/astrozodiac/planets',
    status: 'available',
    isFree: false,
    gradient: 'from-purple-500 to-indigo-500',
    features: ['Mars = Action', 'Vénus = Amour', 'Saturne = Structure']
  },
  {
    id: 'houses',
    title: 'Domaines de Vie',
    subtitle: '12 maisons = 12 secteurs',
    description: 'Carrière, relations, finances, santé...',
    icon: LayoutGrid,
    path: '/astrozodiac/houses',
    status: 'available',
    isFree: false,
    gradient: 'from-cyan-500 to-teal-500',
    features: ['Radar de vie', 'Zones actives', 'Priorités']
  },
  {
    id: 'aspects',
    title: 'Mécanique Interne',
    subtitle: 'Aspects entre planètes',
    description: 'Tes tensions et fluidités intérieures',
    icon: Compass,
    path: '/astrozodiac/aspects',
    status: 'available',
    isFree: false,
    gradient: 'from-rose-500 to-pink-500',
    features: ['Zones fluides', 'Défis évolutifs', 'Patterns']
  },
  {
    id: 'transits',
    title: 'Transits & Timing',
    subtitle: 'Climat céleste actuel',
    description: 'Tes fenêtres d\'opportunité en temps réel',
    icon: CloudSun,
    path: '/astrozodiac/transits',
    status: 'available',
    isFree: false,
    gradient: 'from-amber-400 to-yellow-500',
    features: ['Météo céleste', 'Transits actifs', 'Timing stratégique']
  },
  {
    id: 'decision',
    title: 'Mode Décision',
    subtitle: 'Aide à la décision',
    description: 'Évalue tes choix avec les astres',
    icon: Target,
    path: '/astrozodiac/decision',
    status: 'available',
    isFree: false,
    gradient: 'from-violet-500 to-purple-600',
    features: ['Score fluidité/friction', 'Timing optimal', 'Recommandations']
  }
];

const ModuleCard = ({ module, index }) => {
  const Icon = module.icon;
  const isAvailable = module.status === 'available';
  
  const cardContent = (
    <div className={`
      relative overflow-hidden rounded-2xl h-full
      bg-white border border-sky-200
      transition-all duration-300
      ${isAvailable ? 'hover:border-sky-400 hover:shadow-lg hover:shadow-sky-100 hover:scale-[1.02]' : 'opacity-60'}
    `}>
      <div className="relative p-6 flex flex-col h-full">
        {/* Status Badge */}
        <div className="absolute top-4 right-4">
          {module.isPremiumHighlight ? (
            <span className="px-2 py-1 text-xs font-semibold bg-gradient-to-r from-amber-500 to-rose-500 text-white rounded-full flex items-center gap-1 shadow-lg">
              <Crown className="h-3 w-3" />
              Premium
            </span>
          ) : module.isFree ? (
            <span className="px-2 py-1 text-xs font-semibold bg-sky-100 text-sky-700 rounded-full">
              Gratuit
            </span>
          ) : isAvailable ? (
            <span className="px-2 py-1 text-xs font-medium bg-amber-100 text-amber-700 rounded-full flex items-center gap-1">
              <Lock className="h-3 w-3" />
              Premium
            </span>
          ) : (
            <span className="px-2 py-1 text-xs bg-gray-100 text-gray-500 rounded-full">
              Bientôt
            </span>
          )}
        </div>
        
        {/* Icon */}
        <div className={`
          relative mb-4 p-4 rounded-xl w-fit
          bg-gradient-to-br ${module.gradient}
          shadow-lg
        `}>
          <Icon className="h-7 w-7 text-white" strokeWidth={1.5} />
          {!isAvailable && (
            <div className="absolute inset-0 flex items-center justify-center rounded-xl bg-black/30">
              <Lock className="h-4 w-4 text-white" />
            </div>
          )}
        </div>
        
        {/* Title */}
        <h3 className="text-lg font-bold text-gray-900 mb-1">{module.title}</h3>
        <p className="text-sky-600 text-sm font-medium mb-2">{module.subtitle}</p>
        
        {/* Description */}
        <p className="text-gray-600 text-sm mb-4">{module.description}</p>
        
        {/* Features */}
        <div className="flex-grow">
          <ul className="space-y-1">
            {module.features.map((f, i) => (
              <li key={i} className="text-xs text-gray-500 flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-sky-400 rounded-full" />
                {f}
              </li>
            ))}
          </ul>
        </div>
        
        {/* Action */}
        {isAvailable && (
          <div className="mt-4 pt-4 border-t border-gray-100">
            <span className="text-sky-600 text-sm font-medium flex items-center gap-1 group-hover:gap-2 transition-all">
              Explorer
              <ArrowRight className="h-4 w-4" />
            </span>
          </div>
        )}
      </div>
    </div>
  );
  
  if (isAvailable && module.path) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: index * 0.1 }}
        className="group"
        data-testid={`module-card-${module.id}`}
      >
        <Link to={module.path} className="block h-full" data-testid={`explore-${module.id}-btn`}>
          {cardContent}
        </Link>
      </motion.div>
    );
  }
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.1 }}
      className="cursor-not-allowed"
      data-testid={`module-card-${module.id}`}
    >
      {cardContent}
    </motion.div>
  );
};

const AstrozodiacHomePage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, logout } = useAuth();
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-50 via-white to-cyan-50">
      {/* Subtle decorative elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 right-20 w-64 h-64 bg-sky-200/30 rounded-full blur-3xl" />
        <div className="absolute bottom-20 left-20 w-80 h-80 bg-cyan-200/20 rounded-full blur-3xl" />
      </div>
      
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-sky-100">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link 
              to="/" 
              className="flex items-center gap-2 text-sky-600 hover:text-sky-700 transition-colors"
              data-testid="back-to-reveliae"
            >
              <ChevronLeft className="h-5 w-5" />
              <span className="font-semibold">REVELIAE</span>
            </Link>
            <span className="text-gray-300">/</span>
            <div className="flex items-center gap-2">
              <Star className="h-5 w-5 text-sky-600" />
              <span className="font-bold text-gray-900 tracking-wide">ASTROZODIAC</span>
            </div>
          </div>
          
          <div>
            {isAuthenticated ? (
              <div className="flex items-center gap-3">
                <span className="text-gray-600 text-sm hidden sm:block">
                  {user?.first_name || user?.name}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={logout}
                  className="border-sky-200 text-sky-700 hover:bg-sky-50 rounded-xl"
                >
                  <User className="h-4 w-4 mr-2" />
                  Déconnexion
                </Button>
              </div>
            ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/login')}
                className="border-sky-200 text-sky-700 hover:bg-sky-50 rounded-xl"
                data-testid="login-btn"
              >
                <LogIn className="h-4 w-4 mr-2" />
                Connexion
              </Button>
            )}
          </div>
        </div>
      </header>
      
      {/* Hero */}
      <section className="relative py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-sky-100 rounded-full border border-sky-200 mb-6">
              <Star className="h-4 w-4 text-sky-600" />
              <span className="text-sky-700 text-sm font-medium">Astro Engine</span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4">
              <span className="text-gray-900">Ton tableau de bord</span>
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-sky-500 via-cyan-500 to-teal-500">
                stratégique céleste
              </span>
            </h1>
            
            <p className="text-gray-600 text-lg max-w-2xl mx-auto mb-8 leading-relaxed">
              Pas un horoscope. Un outil d'aide à la décision basé sur les cycles célestes.
              Comprends tes mécanismes, anticipe tes climats, agis au bon moment.
            </p>
          </motion.div>
        </div>
      </section>
      
      {/* Modules Grid */}
      <section className="py-8 px-4 pb-12">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {MODULES.map((module, index) => (
              <ModuleCard key={module.id} module={module} index={index} />
            ))}
          </div>
          
          {/* Coming Soon */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="mt-12 text-center"
          >
            <div className="inline-flex items-center gap-3 px-6 py-3 bg-sky-50 rounded-2xl border border-sky-200">
              <Sparkles className="h-5 w-5 text-sky-500" />
              <span className="text-gray-600">
                <span className="text-sky-600 font-semibold">Bientôt :</span> Connexion LUMINA • Synastrie (compatibilité)
              </span>
            </div>
          </motion.div>
          
          {/* Quick Actions - Quiz, Knowledge, Chat */}
          <QuizDiscoverChatSection
            theme="sky"
            appName="astrozodiac"
            quizPath="/astrozodiac/quiz"
            quizTitle="Quiz Astrologie"
            quizDescription="Teste tes connaissances sur les signes et planètes"
            quizFeatures={['20 questions', 'Explications détaillées', 'Score final']}
            discoverPath="/astrozodiac/knowledge"
            discoverTitle="Découvrir"
            discoverDescription="Apprends les bases de l'astrologie"
            discoverFeatures={['Les 12 signes', 'Les planètes', 'Les maisons']}
            discoverIsPremium={true}
            chatPath="/astrozodiac/chat"
            chatTitle="Poser une Question"
            chatDescription="Discute avec Stella, notre experte astro IA"
            chatExpertName="Stella"
            chatFeatures={['Chat IA', 'Réponses personnalisées', 'Conseils astrologiques']}
          />
        </div>
      </section>

      {/* Playlist Toggle */}
      <PlaylistToggle theme="sky" app="astrozodiac" />
    </div>
  );
};

export default AstrozodiacHomePage;
